import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Plus, Edit, Trash2, Calendar, DollarSign, User, HandCoins, RefreshCw, Wallet, X, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

export default function CashBookings() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [theaters, setTheaters] = useState([]);
  const [filteredTheaters, setFilteredTheaters] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [handoverDialog, setHandoverDialog] = useState(false);
  const [resetDialog, setResetDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [resetBranchId, setResetBranchId] = useState('');
  
  // Filter states
  const [filterBranch, setFilterBranch] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    id: '',
    trn_no: '',
    booking_date: new Date().toISOString().split('T')[0],
    branch_id: user?.branch_id || '',
    theater_id: '',
    time_slot: '',
    advance_cash: 0,
    done_by: '',
  });

  // Handover form state
  const [handoverFormData, setHandoverFormData] = useState({
    handed_to: '',
    date: new Date().toISOString().split('T')[0],
    note: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Filter bookings when filters change
    fetchBookings();
  }, [filterBranch, filterStartDate, filterEndDate]);

  useEffect(() => {
    // Filter theaters when branch changes
    if (formData.branch_id) {
      const filtered = theaters.filter(t => t.branch_id === formData.branch_id);
      setFilteredTheaters(filtered);
    } else {
      setFilteredTheaters([]);
    }
  }, [formData.branch_id, theaters]);

  useEffect(() => {
    // Load time slots when theater is selected
    if (formData.theater_id) {
      fetchTimeSlots(formData.theater_id);
    } else {
      setTimeSlots([]);
    }
  }, [formData.theater_id]);

  const fetchData = async () => {
    try {
      const [branchesRes, theatersRes] = await Promise.all([
        api.get('/branches'),
        api.get('/theaters'),
      ]);
      setBranches(branchesRes.data);
      setTheaters(theatersRes.data);
      
      // Auto-select branch for manager/regional_manager
      if (['manager', 'regional_manager'].includes(user?.role) && user?.branch_id) {
        setFilterBranch(user.branch_id);
      }
      
      fetchBookings();
      fetchHandovers();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    }
  };

  const fetchHandovers = async () => {
    try {
      const res = await api.get(`/cash-booking-handovers?_t=${Date.now()}`);
      setHandovers(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Error fetching handovers:', error);
      toast.error('Failed to load handovers');
    }
  };

  const fetchBookings = async () => {
    try {
      const params = new URLSearchParams();
      if (filterBranch) params.append('branch_id', filterBranch);
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      
      // Add cache busting parameter
      params.append('_t', Date.now().toString());
      
      const res = await api.get(`/cash-bookings?${params.toString()}`);
      setBookings(res.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    }
  };

  const fetchTimeSlots = async (theaterId) => {
    try {
      // Find theater name from the selected theater
      const theater = theaters.find(t => t.id === theaterId);
      if (!theater) return;
      
      const response = await api.get(`/theater-slots/${theater.name}`);
      setTimeSlots(response.data);
    } catch (error) {
      console.error('Error fetching time slots:', error);
      toast.error('Failed to load time slots');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const bookingData = {
        trn_no: formData.trn_no,
        booking_date: formData.booking_date,
        branch_id: formData.branch_id,
        theater_id: formData.theater_id,
        time_slot: formData.time_slot,
        advance_cash: parseFloat(formData.advance_cash),
        done_by: formData.done_by
      };

      if (formData.id) {
        await api.put(`/cash-bookings/${formData.id}`, bookingData);
        toast.success('Booking updated successfully');
      } else {
        await api.post('/cash-bookings', bookingData);
        toast.success('Booking created successfully');
      }

      setDialog(false);
      resetForm();
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save booking');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (booking) => {
    setFormData({
      id: booking.id,
      trn_no: booking.trn_no,
      booking_date: booking.booking_date,
      branch_id: booking.branch_id,
      theater_id: booking.theater_id,
      time_slot: booking.time_slot,
      advance_cash: booking.advance_cash,
      done_by: booking.done_by,
    });
    setDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;

    try {
      await api.delete(`/cash-bookings/${id}`);
      toast.success('Booking deleted successfully');
      fetchBookings();
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete booking');
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      trn_no: '',
      booking_date: new Date().toISOString().split('T')[0],
      branch_id: user?.branch_id || '',
      theater_id: '',
      time_slot: '',
      advance_cash: 0,
      done_by: '',
    });
  };

  const handleBookingSelect = (bookingId) => {
    setSelectedBookings(prev => {
      if (prev.includes(bookingId)) {
        return prev.filter(id => id !== bookingId);
      } else {
        return [...prev, bookingId];
      }
    });
  };

  const handleSelectAll = () => {
    const activeUncollectedBookings = bookings.filter(b => !b.collected && b.status === 'active');
    if (selectedBookings.length === activeUncollectedBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(activeUncollectedBookings.map(b => b.id));
    }
  };

  const getSelectedTotal = () => {
    return bookings
      .filter(b => selectedBookings.includes(b.id))
      .reduce((sum, b) => sum + b.advance_cash, 0);
  };

  const handleSubmitHandover = async (e) => {
    e.preventDefault();
    
    if (selectedBookings.length === 0) {
      toast.error('Please select at least one booking');
      return;
    }

    try {
      await api.post('/cash-booking-handovers', {
        booking_ids: selectedBookings,
        handed_to: handoverFormData.handed_to,
        date: handoverFormData.date,
        note: handoverFormData.note || null
      });
      
      toast.success('Cash booking handover recorded successfully');
      setHandoverDialog(false);
      setSelectedBookings([]);
      setHandoverFormData({
        handed_to: '',
        date: new Date().toISOString().split('T')[0],
        note: ''
      });
      fetchBookings();
      fetchHandovers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record handover');
    }
  };

  const handleResetCollection = async () => {
    if (!resetBranchId) {
      toast.error('Please select a branch');
      return;
    }

    if (!window.confirm('Are you sure you want to DELETE ALL booking records for this branch? This action cannot be undone.')) {
      return;
    }

    try {
      await api.post('/cash-booking-handovers/reset', null, {
        params: { branch_id: resetBranchId }
      });
      
      toast.success('All booking records deleted successfully');
      setResetDialog(false);
      setResetBranchId('');
      
      // Add a small delay to ensure backend processing is complete
      setTimeout(() => {
        fetchBookings();
        fetchHandovers();
      }, 500);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset collection');
    }
  };

  const handleCancel = (booking) => {
    setBookingToCancel(booking);
    setCancellationReason('');
    setCancelDialog(true);
  };

  const handleCancelBooking = async (reason) => {
    if (!bookingToCancel) return;

    try {
      await api.put(`/cash-bookings/${bookingToCancel.id}/cancel`, {
        reason: reason || 'No reason provided'
      });
      
      toast.success('Booking cancelled successfully');
      setCancelDialog(false);
      setBookingToCancel(null);
      setCancellationReason('');
      setCancellationReason('');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleReactivate = async (bookingId) => {
    if (!window.confirm('Are you sure you want to reactivate this cancelled booking?')) {
      return;
    }

    try {
      await api.put(`/cash-bookings/${bookingId}/reactivate`);
      toast.success('Booking reactivated successfully');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate booking');
    }
  };

  const getTotalCash = () => {
    return bookings.filter(b => b.status === 'active').reduce((sum, b) => sum + (b.advance_cash || 0), 0);
  };

  const getUncollectedTotal = () => {
    return bookings.filter(b => !b.collected && b.status === 'active').reduce((sum, b) => sum + b.advance_cash, 0);
  };

  const getCancelledTotal = () => {
    return bookings.filter(b => b.status === 'cancelled').reduce((sum, b) => sum + b.advance_cash, 0);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Branch Bookings</h1>
          <p className="text-slate-600 mt-1">Manage on-the-spot branch bookings and handovers</p>
        </div>
        <div className="flex gap-2">
          {user?.role === 'admin' && (
            <Dialog open={resetDialog} onOpenChange={setResetDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2 border-red-200 text-red-600 hover:bg-red-50">
                  <RefreshCw size={16} />
                  Reset Collection
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Reset Branch Booking Collection</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    This will <strong>DELETE ALL</strong> booking records and handover records for the selected branch. All amounts will become zero.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="reset_branch">Select Branch *</Label>
                    <Select value={resetBranchId} onValueChange={setResetBranchId}>
                      <SelectTrigger id="reset_branch">
                        <SelectValue placeholder="Select branch" />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map(branch => (
                          <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={handleResetCollection}
                    className="w-full bg-red-600 hover:bg-red-700"
                  >
                    Reset Collection
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}

          <Dialog open={handoverDialog} onOpenChange={setHandoverDialog}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-green-600 hover:bg-green-700"
                disabled={bookings.filter(b => !b.collected).length === 0}
              >
                <HandCoins size={16} />
                Record Handover
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Record Branch Booking Handover</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmitHandover} className="space-y-4">
                {/* Selection Summary */}
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <Label className="text-sm text-green-800">Selected Bookings</Label>
                      <p className="text-lg font-bold text-green-600">{selectedBookings.length}</p>
                    </div>
                    <div className="text-right">
                      <Label className="text-sm text-green-800">Total Amount</Label>
                      <p className="text-2xl font-bold text-green-600">₹{getSelectedTotal().toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {/* Bookings Selection */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label>Select Bookings to Handover</Label>
                    {bookings.filter(b => !b.collected).length > 0 && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={handleSelectAll}
                      >
                        {selectedBookings.length === bookings.filter(b => !b.collected).length ? 'Deselect All' : 'Select All'}
                      </Button>
                    )}
                  </div>
                  
                  <div className="border rounded-lg max-h-64 overflow-y-auto">
                    {bookings.filter(b => !b.collected && b.status === 'active').length === 0 ? (
                      <div className="p-8 text-center text-slate-500">
                        No uncollected bookings available
                      </div>
                    ) : (
                      <div className="divide-y">
                        {bookings.filter(b => !b.collected && b.status === 'active').map(booking => (
                          <div 
                            key={booking.id}
                            className="p-3 hover:bg-slate-50 flex items-start gap-3"
                          >
                            <Checkbox 
                              checked={selectedBookings.includes(booking.id)}
                              onCheckedChange={() => handleBookingSelect(booking.id)}
                            />
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleBookingSelect(booking.id)}>
                              <div className="flex justify-between items-start gap-2">
                                <div>
                                  <p className="font-medium text-slate-900">TRN: {booking.trn_no}</p>
                                  <p className="text-sm text-slate-600">
                                    {booking.theater_name} • {booking.time_slot}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {booking.booking_date} • By: {booking.done_by}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-bold text-green-600">₹{booking.advance_cash.toFixed(2)}</p>
                                  <p className="text-xs text-slate-500">{booking.branch_name}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Handover Details */}
                <div className="space-y-2">
                  <Label htmlFor="handed_to">Handed Over To *</Label>
                  <Input
                    id="handed_to"
                    value={handoverFormData.handed_to}
                    onChange={(e) => setHandoverFormData({ ...handoverFormData, handed_to: e.target.value })}
                    placeholder="Enter admin name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="handover_date">Date *</Label>
                  <Input
                    id="handover_date"
                    type="date"
                    value={handoverFormData.date}
                    onChange={(e) => setHandoverFormData({ ...handoverFormData, date: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="handover_note">Note (Optional)</Label>
                  <Textarea
                    id="handover_note"
                    value={handoverFormData.note}
                    onChange={(e) => setHandoverFormData({ ...handoverFormData, note: e.target.value })}
                    placeholder="Add any additional notes"
                    rows={3}
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={selectedBookings.length === 0}
                >
                  Record Handover (₹{getSelectedTotal().toFixed(2)})
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={dialog} onOpenChange={setDialog}>
            <DialogTrigger asChild>
              <Button onClick={resetForm} className="gap-2">
                <Plus size={16} /> New Booking
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{formData.id ? 'Edit' : 'New'} Branch Booking</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="trn_no">TRN No *</Label>
                  <Input
                    id="trn_no"
                    value={formData.trn_no}
                    onChange={(e) => setFormData({ ...formData, trn_no: e.target.value })}
                    placeholder="Enter transaction number"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="booking_date">Booking Date *</Label>
                  <Input
                    id="booking_date"
                    type="date"
                    value={formData.booking_date}
                    onChange={(e) => setFormData({ ...formData, booking_date: e.target.value })}
                    required
                  />
                </div>

                {user?.role === 'admin' && (
                  <div className="space-y-2">
                    <Label>Branch *</Label>
                    <Select
                      value={formData.branch_id}
                      onValueChange={(value) => setFormData({ ...formData, branch_id: value, theater_id: '' })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                      <SelectContent>
                        {branches.map(branch => (
                          <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Theater *</Label>
                  <Select
                    value={formData.theater_id}
                    onValueChange={(value) => setFormData({ ...formData, theater_id: value, time_slot: '' })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select theater" /></SelectTrigger>
                    <SelectContent>
                      {filteredTheaters.map(theater => (
                        <SelectItem key={theater.id} value={theater.id}>{theater.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Time Slot *</Label>
                  <Select
                    value={formData.time_slot}
                    onValueChange={(value) => setFormData({ ...formData, time_slot: value })}
                    disabled={!formData.theater_id}
                  >
                    <SelectTrigger><SelectValue placeholder="Select time slot" /></SelectTrigger>
                    <SelectContent>
                      {timeSlots.length === 0 && formData.theater_id && (
                        <SelectItem value="no-slots" disabled>No slots available</SelectItem>
                      )}
                      {timeSlots.map(slot => (
                        <SelectItem key={slot.id} value={slot.time_slot}>{slot.time_slot}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!formData.theater_id && (
                    <p className="text-xs text-slate-500">Select a theater first</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="advance_cash">Advance Cash (₹) *</Label>
                  <Input
                    id="advance_cash"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.advance_cash}
                    onChange={(e) => setFormData({ ...formData, advance_cash: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>

                <div className="space-y-2 col-span-2">
                  <Label htmlFor="done_by">Done By *</Label>
                  <Input
                    id="done_by"
                    value={formData.done_by}
                    onChange={(e) => setFormData({ ...formData, done_by: e.target.value })}
                    placeholder="Enter name of person who handled booking"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Booking'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {user?.role === 'admin' && (
              <div className="space-y-2">
                <Label>Branch</Label>
                <Select value={filterBranch || 'all'} onValueChange={(value) => setFilterBranch(value === 'all' ? '' : value)}>
                  <SelectTrigger><SelectValue placeholder="All branches" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-600 rounded-xl">
                <Wallet className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-orange-800 font-medium">Uncollected</p>
                <p className="text-2xl font-bold text-orange-900">₹{getUncollectedTotal().toFixed(2)}</p>
                <p className="text-xs text-orange-700">{bookings.filter(b => !b.collected && b.status === 'active').length} bookings</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-600 rounded-xl">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-800 font-medium">Total Amount</p>
                <p className="text-2xl font-bold text-green-900">₹{getTotalCash().toFixed(2)}</p>
                <p className="text-xs text-green-700">{bookings.filter(b => b.status === 'active').length} active bookings</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-600 rounded-xl">
                <HandCoins className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-800 font-medium">Collected</p>
                <p className="text-2xl font-bold text-blue-900">₹{(getTotalCash() - getUncollectedTotal()).toFixed(2)}</p>
                <p className="text-xs text-blue-700">{bookings.filter(b => b.collected && b.status === 'active').length} bookings</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-600 rounded-xl">
                <X className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-red-800 font-medium">Cancelled</p>
                <p className="text-2xl font-bold text-red-900">₹{getCancelledTotal().toFixed(2)}</p>
                <p className="text-xs text-red-700">{bookings.filter(b => b.status === 'cancelled').length} bookings</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bookings List */}
      <Card>
        <CardHeader>
          <CardTitle>Branch Bookings ({bookings.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {bookings.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No bookings found</p>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div key={booking.id} className={`border rounded-lg p-4 hover:bg-slate-50 
                  ${booking.status === 'cancelled' ? 'opacity-60 bg-red-50 border-red-200' : ''}
                  ${booking.collected && booking.status === 'active' ? 'opacity-75 bg-blue-50' : ''}`}>
                  <div className="flex justify-between items-start">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-4">
                        <h3 className="font-semibold text-lg">TRN: {booking.trn_no}</h3>
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                          ₹{booking.advance_cash.toFixed(2)}
                        </span>
                        
                        {/* Status badges */}
                        {booking.status === 'cancelled' && (
                          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                            ✕ Cancelled
                          </span>
                        )}
                        {booking.collected && booking.status === 'active' && (
                          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                            ✓ Collected
                          </span>
                        )}
                        {!booking.collected && booking.status === 'active' && (
                          <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                            Pending
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-600">Date</p>
                          <p className="font-medium flex items-center gap-1">
                            <Calendar size={14} /> {booking.booking_date}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-600">Branch</p>
                          <p className="font-medium">{booking.branch_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Theater</p>
                          <p className="font-medium">{booking.theater_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Time Slot</p>
                          <p className="font-medium">{booking.time_slot}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <User size={14} />
                        <span>Done by: <span className="font-medium text-slate-900">{booking.done_by}</span></span>
                      </div>
                      
                      <div className="text-xs text-slate-500">
                        Created by {booking.created_by_username} on {new Date(booking.created_at).toLocaleString()}
                      </div>

                      {/* Cancellation details */}
                      {booking.status === 'cancelled' && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm">
                          <p className="text-red-800 font-medium">Cancelled by: {booking.cancelled_by}</p>
                          <p className="text-red-600">Date: {new Date(booking.cancelled_at).toLocaleString()}</p>
                          <p className="text-red-600">Reason: {booking.cancellation_reason}</p>
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-col gap-2 ml-4">
                      {booking.status === 'active' && !booking.collected && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => handleCancel(booking)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <X size={16} />
                        </Button>
                      )}
                      
                      {booking.status === 'cancelled' && user?.role === 'admin' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleReactivate(booking.id)}
                          className="text-green-600 hover:bg-green-50"
                        >
                          <RotateCcw size={16} />
                        </Button>
                      )}
                      
                      {user?.role === 'admin' && booking.status === 'active' && (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleEdit(booking)}>
                            <Edit size={16} />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(booking.id)} className="text-red-600">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Handover History */}
      {handovers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Handover History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {handovers.map((handover) => (
                <div key={handover.id} className="p-4 border rounded-lg bg-white">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                          {handover.booking_ids.length} booking{handover.booking_ids.length !== 1 ? 's' : ''}
                        </span>
                        <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded">
                          {handover.branch_name}
                        </span>
                      </div>
                      <p className="font-medium text-slate-900">Handed to: {handover.handed_to}</p>
                      <p className="text-sm text-slate-600">Date: {handover.date}</p>
                      {handover.note && (
                        <p className="text-sm text-slate-500 italic">Note: {handover.note}</p>
                      )}
                      <p className="text-xs text-slate-500">
                        Recorded by: {handover.created_by_username} • {new Date(handover.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">₹{handover.amount.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cancellation Dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Are you sure you want to cancel this booking?
            </p>
            {bookingToCancel && (
              <div className="p-3 bg-slate-50 rounded border">
                <p className="font-medium">TRN: {bookingToCancel.trn_no}</p>
                <p className="text-sm text-slate-600">
                  {bookingToCancel.theater_name} • {bookingToCancel.time_slot}
                </p>
                <p className="text-sm text-slate-600">
                  Amount: ₹{bookingToCancel.advance_cash}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="cancellation_reason">Reason for Cancellation</Label>
              <Textarea
                id="cancellation_reason"
                placeholder="Enter reason for cancellation..."
                rows={3}
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setCancelDialog(false);
                  setBookingToCancel(null);
                  setCancellationReason('');
                }}
              >
                Keep Booking
              </Button>
              <Button 
                onClick={() => handleCancelBooking(cancellationReason)}
                className="bg-red-600 hover:bg-red-700"
              >
                Cancel Booking
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
